import React, { useEffect } from 'react';
import queryString from 'query-string';
import { updateUrl } from '@/utils';

type SelectedWorkpack = { id?: string; rowId?: string };

export const useInsightsTabUrlSync = (
  selectedTab: number,
  setSelectedTab: React.Dispatch<React.SetStateAction<number>>
) => {
  // Read from URL on mount
  useEffect(() => {
    const parsed = queryString.parse(window.location.search, { arrayFormat: 'comma' });
    const tab = parsed.tab;
    if (tab) {
      setSelectedTab(Number(tab));
    }
  }, [setSelectedTab]);

  // Write to URL on state change
  useEffect(() => {
    updateUrl('tab', JSON.stringify(selectedTab));
  }, [selectedTab]);
};

export const useViewByUrlSync = <T extends string>(
  viewBy: T,
  setViewBy: React.Dispatch<React.SetStateAction<T>>,
  validValues?: readonly T[]
) => {
  // Read from URL on mount
  useEffect(() => {
    const parsed = queryString.parse(window.location.search, { arrayFormat: 'comma' });
    const viewByParam = parsed.viewBy;
    if (viewByParam && typeof viewByParam === 'string') {
      if (!validValues || validValues.includes(viewByParam as T)) {
        setViewBy(viewByParam as T);
      }
    }
  }, [setViewBy, validValues]);

  // Write to URL on state change
  useEffect(() => {
    updateUrl('viewBy', viewBy);
  }, [viewBy]);
};

export const useSelectedWorkpackUrlSync = (
  selectedWorkpack: SelectedWorkpack,
  setSelectedWorkpack: React.Dispatch<React.SetStateAction<SelectedWorkpack>>
) => {
  // Read from URL on mount
  useEffect(() => {
    const parsed = queryString.parse(window.location.search, { arrayFormat: 'comma' });
    const workpackId = parsed.workpackId;
    if (workpackId && typeof workpackId === 'string') {
      const isUnassigned = workpackId === 'unassigned';
      setSelectedWorkpack({
        id: isUnassigned ? 'unassigned' : workpackId,
        rowId: isUnassigned ? '' : workpackId,
      });
    }
  }, [setSelectedWorkpack]);

  // Write to URL on state change
  useEffect(() => {
    if (!selectedWorkpack.id) {
      updateUrl('workpackId', null);
    } else {
      updateUrl('workpackId', selectedWorkpack.id);
    }
  }, [selectedWorkpack.id]);
};

export const useKpiWorkpackUrlSync = (
  workpackId: string | undefined,
  setWorkpackId: (id: string | undefined) => void,
  isOpen: boolean,
  setIsOpen: (open: boolean) => void
) => {
  // Read from URL on mount
  useEffect(() => {
    const parsed = queryString.parse(window.location.search, { arrayFormat: 'comma' });
    const kpiWorkpackId = parsed.kpiWorkpackId;
    if (kpiWorkpackId && typeof kpiWorkpackId === 'string') {
      setWorkpackId(kpiWorkpackId);
      setIsOpen(true);
    }
  }, [setWorkpackId, setIsOpen]);

  // Write to URL on state change
  useEffect(() => {
    if (!isOpen || !workpackId) {
      updateUrl('kpiWorkpackId', null);
    } else {
      updateUrl('kpiWorkpackId', workpackId);
    }
  }, [workpackId, isOpen]);
};
